/**
 * The refusal a save gets when required fields are empty.
 *
 * <p>A toast was the wrong shape for this. It is the ANSWER to a button the
 * user just pressed, and it faded before they looked back — so the save
 * appeared to do nothing at all. This is a blocking dialog (Tier 1, §18.6): it
 * stays until acknowledged, names every missing field as a list rather than a
 * comma-run, and can put the user IN the first one.</p>
 *
 * <p>The field a refusal points at travels WITH the rule that found it — each
 * entry carries its own anchor — so the list of what is missing and the thing
 * the dialog jumps to cannot drift apart. (Prototype v46 keeps a separate
 * label→id map, `NEED_FIELD`, which has to be edited in step with the rule and
 * silently points nowhere when it is not.)</p>
 *
 * <p>Scoped to the Client Request module on purpose (§18.2): only its two forms
 * use it today. It takes no module-specific import, so promoting it to
 * `components/` the day a second module needs it is a file move.</p>
 */
import { showDialog } from '../../components/confirm';

/** One unmet requirement, with the place it lives. */
export interface MissingField {
  /** What the user sees — the SAME wording as the field's own label. */
  label: string;
  /**
   * `id` of the field's row (`.sd-row`), so the refusal can point at it.
   * Omitted for a blocker that is not a single field on this page (for
   * example "At least one Service Task — none added").
   */
  anchorId?: string;
}

/**
 * The labels of a missing-field list, comma-joined, for prose.
 *
 * <p>Exists because `MissingField[].join(', ')` is LEGAL TypeScript and renders
 * "[object Object], [object Object]" — the compiler cannot catch it, so it
 * shipped to the release checklist and to two tooltips before anyone saw it
 * (2026-09-10). Anything putting this list into a sentence should call this
 * rather than joining, so the mistake has one obvious answer.</p>
 */
export function labels(fields: readonly MissingField[]): string {
  return fields.map(f => f.label).join(', ');
}

/** The class that rings a field the dialog is pointing at. */
const HOT = 'cr-hot';

/**
 * Ring a field, scroll it into view, and optionally put the cursor in it.
 *
 * <p>The ring clears itself on the first edit, or after a few seconds, because
 * it is a pointer and not a verdict — leaving it on would mark a field the user
 * has already dealt with.</p>
 */
function pointAt(anchorId: string, focus: boolean): void {
  const row = document.getElementById(anchorId);
  if (!row) return;

  row.classList.add(HOT);
  row.scrollIntoView({ block: 'center', behavior: 'smooth' });

  if (focus) {
    row.querySelector<HTMLElement>(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), '
      + '[contenteditable="true"], button:not([disabled])',
    )?.focus();
  }

  // Registered AFTER focus() so the dialog's own jump does not immediately
  // clear the ring it just drew.
  const clear = () => {
    row.classList.remove(HOT);
    window.clearTimeout(timer);
    row.removeEventListener('input', clear);
    row.removeEventListener('change', clear);
  };
  const timer = window.setTimeout(clear, 6000);
  row.addEventListener('input', clear);
  row.addEventListener('change', clear);
}

export interface RefuseSaveOptions {
  /** What was refused, as the user would name it: 'request', 'service'. */
  subject: string;
  /** Override the title (the release gate is not a "save"). */
  title?: string;
  /** Override the lead line above the list. */
  message?: string;
}

/**
 * Refuse a save and show the user exactly what is missing.
 *
 * <p>Resolves once the dialog is dismissed. Callers just `return` afterwards —
 * the refusal has already been delivered.</p>
 */
export async function refuseSave(
  missing: MissingField[],
  { subject, title, message }: RefuseSaveOptions,
): Promise<void> {
  if (!missing.length) return;

  const one = missing.length === 1;
  // Only an entry that IS a field on this page can be jumped to; a blocker
  // like "no Service Task added" has nowhere to point.
  const target = missing.find(m => m.anchorId);

  // Ring the first one straight away, so the form already shows where to look
  // while the dialog is still open — the answer is in two places at once.
  if (target?.anchorId) pointAt(target.anchorId, false);

  const answer = await showDialog({
    type: 'warning',
    title: title
      ?? `Not saved — ${missing.length} required field${one ? ' is' : 's are'} empty`,
    // A service that was never saved has no Service ID yet, and that is the
    // thing the user is about to go looking for — so the refusal says it, and
    // names the button that mints it (v68 `refuseTask`).
    message: message
      ?? (subject === 'service'
        ? `The service has not been saved and has no Service ID yet. `
          + `Fill ${one ? 'this' : 'these'} in and press Save Service again:`
        : `The ${subject} has not been saved. Fill ${one ? 'this' : 'these'} in and save again:`),
    list: missing.map(m => m.label),
    actions: target
      ? [{ label: 'Close', value: 'close' },
         { label: `Go to ${target.label}`, primary: true, value: 'goto' }]
      : [{ label: 'Close', primary: true, value: 'close' }],
  });

  if (answer === 'goto' && target?.anchorId) pointAt(target.anchorId, true);
}
